import {
    Component,
    Input,
    Output,
    ViewChild,
    EventEmitter,
    ViewChildren,
    QueryList,
} from '@angular/core';
import { MapInfoWindow, MapMarker } from '@angular/google-maps';
import { environment } from '@env/environment';
import { NGXLogger } from 'ngx-logger';
import { CenterDetailsComponent } from '../center-details/center-details.component';

@Component({
    selector: 'app-map-component',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.scss'],
})
export class MapComponent {
    @ViewChild(MapInfoWindow) infoWindow: MapInfoWindow;
    @ViewChildren('marker') markers: QueryList<MapMarker>;
    @ViewChildren('centerDetails')
    centerDetails: QueryList<CenterDetailsComponent>;
    @Input() apiLoaded: boolean;
    @Input() medicalCenters: any[];
    @Output() markerClicked: EventEmitter<any> = new EventEmitter<any>();
    @Output() callBack: EventEmitter<any> = new EventEmitter<any>();
    @Output() appointmentClicked: EventEmitter<any> = new EventEmitter<any>();
    defaultZoom = 12;
    zoomConfigArray: any[] = [
        {
            min: 5,
            max: 10,
            zoom: 11,
        },
        {
            min: 10,
            max: 20,
            zoom: 10,
        },
        {
            min: 20,
            max: 40,
            zoom: 9,
        },
        {
            min: 40,
            max: 60,
            zoom: 8,
        },
        {
            min: 60,
            max: null,
            zoom: 7,
        },
    ];

    mapCenter: google.maps.LatLngLiteral;
    zoom: number;
    selectedCenter: any;

    constructor(private logger: NGXLogger) {}

    handleMarkerClick(
        center: any,
        marker?: MapMarker,
        emitClickEvent?: boolean
    ): void {
        this.selectedCenter = center;
        this.mapCenter = this.getLatLngLitteral(center.coordonneesGps);
        this.toggleInfoWindow(center, marker, true);
        if (emitClickEvent) {
            this.markerClicked.emit(this.selectedCenter);
        }
    }

    toggleInfoWindow(center: any, marker?: MapMarker, show?: boolean): void {
        let mapMarker = marker;
        if (!mapMarker) {
            mapMarker = this.getMapMarkerByPosition({
                lat: center.coordonneesGps.latitude,
                lng: center.coordonneesGps.longitude,
            });
        }
        if (show) {
            this.infoWindow.open(mapMarker);
            this.setCenterBtnFocus();
        } else {
            this.infoWindow.close();
        }
    }

    getMarkerIcon(center: any): string {
        if (center.priseRendezVousEnLigne) {
            return `${environment.assetsFolder}assets/images/pin-vert.svg`;
        }
        return `${environment.assetsFolder}assets/images/pin-rose.svg`;
    }

    getLatLngLitteral(coords: {
        latitude: number;
        longitude: number;
    }): google.maps.LatLngLiteral {
        return {
            lat: coords.latitude,
            lng: coords.longitude,
        };
    }

    setMapCenter(position: google.maps.LatLngLiteral): void {
        this.mapCenter = position;
    }

    setMapZoom(listCenters: any): void {
        this.zoom = this.defaultZoom;
        if (listCenters.length > 0) {
            const distanceArray = listCenters.map(
                (center) => center.distanceClient
            );
            if (distanceArray.length > 0) {
                const distanceSum = distanceArray.reduce(
                    (distA, distB) => distA + distB
                );
                const averageDistance = Math.round(
                    distanceSum / distanceArray.length
                );
                const zoomConfig = this.zoomConfigArray.find(
                    (config) =>
                        config.min <= averageDistance &&
                        (averageDistance < config.max || config.max === null)
                );
                if (zoomConfig) {
                    this.zoom = zoomConfig.zoom;
                }
                this.logger.debug(
                    'Average distance/Zoom',
                    averageDistance,
                    '/',
                    this.zoom
                );
            }
        }
    }

    refreshMapData(
        position: google.maps.LatLngLiteral,
        listCenters: any
    ): void {
        this.setMapCenter(position);
        this.setMapZoom(listCenters);
    }

    private setCenterBtnFocus(): void {
        /* if the reference centerDetails is not set yet we subscribe to changes observable
           so that we get notified otherwise we call our child component focus function directly
        */
        if (this.centerDetails.length === 0) {
            this.centerDetails.changes.subscribe((resp) => {
                resp.first.setCenterBtnFocus();
            });
        } else {
            this.centerDetails.first.setCenterBtnFocus();
        }
    }

    private getMapMarkerByPosition(
        position: google.maps.LatLngLiteral
    ): MapMarker {
        return this.markers.find((item) => {
            const markerPosition = item.marker.getPosition().toJSON();
            return (
                markerPosition.lat === position.lat &&
                markerPosition.lng === position.lng
            );
        });
    }
}
